import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  ArrowLeft, AlertCircle, Calendar, Lock, Plus, Scissors, X, Pencil, Share2,
  PlayCircle, SkipBack, SkipForward, Pause, Play,
} from 'lucide-react'
import DashboardLayout from '../components/DashboardLayout'
import VideoPlayer from '../components/VideoPlayer'
import Timeline from '../components/Timeline'
import MomentsList from '../components/MomentsList'
import MomentMarker from '../components/MomentMarker'
import TrimOverlay from '../components/studio/TrimOverlay'
import AnnotationOverlay from '../components/studio/AnnotationOverlay'
import AnnotationLayer from '../components/studio/AnnotationLayer'
import { collectAnnotationPoints, findAnnotationPointAt } from '../utils/annotationSets'
import { useAnnotationStops } from '../hooks/useAnnotationStops'
import ClipsPanel from '../components/studio/ClipsPanel'
import PartageAnnotationsModal from '../components/studio/PartageAnnotationsModal'
import { useAuth } from '../context/AuthContext'
import { hasStudioAccess } from '../utils/planAccess'
import { T, globalStyles } from '../theme'

const API = import.meta.env.VITE_API_URL || 'https://backend-pued.onrender.com/api'

const D     = '#1a1916'
const GOLD  = '#4F8EF7'
const CREAM = '#f5f2eb'
const INK   = '#1a1916'
const MUTED = 'rgba(26,25,22,0.45)'
const MUTED_DARK = 'rgba(245,242,235,0.5)'

// Badges NEW : affiches 2 semaines apres le lancement de chaque feature
const CLIP_CTA_NEW_UNTIL = new Date('2026-07-19T00:00:00Z').getTime() + 14 * 24 * 3600 * 1000
const ANNOTATE_NEW_UNTIL = new Date('2026-07-19T00:00:00Z').getTime() + 14 * 24 * 3600 * 1000
const RULE  = 'rgba(26,25,22,0.09)'
const RED   = '#ef4444'
const RED_CTA = '#E24B4A'   // rouge alerte (CTA "Annuler le decoupage", badge)
const ORANGE = '#f59e0b'
const MONO  = T.mono
const DISP  = T.display

const VIDEO_RETENTION_DAYS = 30

// Duree de l'arret sur une frame annotee, en secondes. Assez pour lire le
// dessin, assez court pour que l'enchainement reste fluide sans cliquer.
const READING_HOLD_SECONDS = 3

// Au-dela de ce bond entre deux frames, ce n'est plus de la lecture mais un
// deplacement : clic dans la timeline, fleche +/-10 s, reprise apres buffering.
// A vitesse 2x une frame avance d'environ 0,03 s, la marge est large.

function authHeaders() {
  const token = localStorage.getItem('insightball_token')
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
}

function computeVideoExpiration(uploadedAt) {
  if (!uploadedAt) return null
  const uploaded = new Date(uploadedAt)
  if (Number.isNaN(uploaded.getTime())) return null
  const expiresAt = new Date(uploaded.getTime() + VIDEO_RETENTION_DAYS * 24 * 3600 * 1000)
  const now = new Date()
  const diffMs = expiresAt.getTime() - now.getTime()
  const daysLeft = Math.ceil(diffMs / (24 * 3600 * 1000))
  return { expiresAt, daysLeft, isExpired: diffMs <= 0 }
}

// mm:ss (ou hh:mm:ss au-dela d'une heure) pour les messages destines au coach.
function formatClock(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return '00:00'
  const total = Math.floor(seconds)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const sec = total % 60
  const mm = String(m).padStart(2, '0')
  const ss = String(sec).padStart(2, '0')
  return h > 0 ? `${String(h).padStart(2, '0')}:${mm}:${ss}` : `${mm}:${ss}`
}

function extractLineup(match) {
  if (!match || !match.lineup) return []
  if (Array.isArray(match.lineup)) return match.lineup
  return [
    ...(match.lineup.starters || []),
    ...(match.lineup.subs || []),
  ]
}

// ─── Etats d'affichage ───────────────────────────────────────────────

function StudioLocked({ reason }) {
  const navigate = useNavigate()
  const message = reason === 'no_user'
    ? 'Connecte-toi pour acceder au studio.'
    : "Ton plan ne donne pas acces au studio video. Passe au plan Coach+ pour debloquer l'analyse video."
  return (
    <DashboardLayout>
      <style>{globalStyles}</style>
      <div style={{
        background: '#fff', border: `1px solid ${RULE}`, borderTop: `2px solid ${GOLD}`,
        padding: 40, textAlign: 'center', maxWidth: 560, margin: '40px auto',
      }}>
        <Lock size={32} color={GOLD} style={{ margin: '0 auto 16px' }} />
        <h2 style={{
          fontFamily: DISP, fontSize: 24, textTransform: 'uppercase',
          color: INK, margin: '0 0 12px', letterSpacing: '.01em',
        }}>
          Studio video
        </h2>
        <p style={{
          fontFamily: T.body, fontSize: 14, color: MUTED, margin: '0 0 24px', lineHeight: 1.5,
        }}>
          {message}
        </p>
        <button
          onClick={() => navigate('/dashboard/settings')}
          style={{
            padding: '12px 24px', background: GOLD, color: D, border: 'none',
            fontFamily: MONO, fontSize: 11, letterSpacing: '.12em',
            textTransform: 'uppercase', fontWeight: 700, cursor: 'pointer',
          }}
        >
          Passer en Coach+
        </button>
      </div>
    </DashboardLayout>
  )
}

function NoVideoState({ matchId }) {
  return (
    <DashboardLayout>
      <style>{globalStyles}</style>
      <Link to={`/dashboard/matches/${matchId}`} style={{
        display: 'flex', alignItems: 'center', gap: 8, fontFamily: MONO,
        fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase',
        color: MUTED, marginBottom: 20, textDecoration: 'none',
      }}>
        <ArrowLeft size={14} /> Retour au match
      </Link>
      <div style={{
        background: '#fff', border: `1px solid ${RULE}`, borderTop: `2px solid ${ORANGE}`,
        padding: 40, textAlign: 'center', maxWidth: 560, margin: '0 auto',
      }}>
        <AlertCircle size={32} color={ORANGE} style={{ margin: '0 auto 16px' }} />
        <h2 style={{
          fontFamily: DISP, fontSize: 22, textTransform: 'uppercase',
          color: INK, margin: '0 0 12px',
        }}>
          Pas de video
        </h2>
        <p style={{
          fontFamily: T.body, fontSize: 14, color: MUTED, margin: 0, lineHeight: 1.5,
        }}>
          Ce match n'a pas de video associee. Telecharge la video du match
          pour pouvoir l'analyser dans le studio.
        </p>
      </div>
    </DashboardLayout>
  )
}

function VideoExpiredState({ matchId, expiresAt }) {
  return (
    <DashboardLayout>
      <style>{globalStyles}</style>
      <Link to={`/dashboard/matches/${matchId}`} style={{
        display: 'flex', alignItems: 'center', gap: 8, fontFamily: MONO,
        fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase',
        color: MUTED, marginBottom: 20, textDecoration: 'none',
      }}>
        <ArrowLeft size={14} /> Retour au match
      </Link>
      <div style={{
        background: '#fff', border: `1px solid ${RULE}`, borderTop: `2px solid ${RED}`,
        padding: 40, textAlign: 'center', maxWidth: 560, margin: '0 auto',
      }}>
        <Calendar size={32} color={RED} style={{ margin: '0 auto 16px' }} />
        <h2 style={{
          fontFamily: DISP, fontSize: 22, textTransform: 'uppercase',
          color: INK, margin: '0 0 12px',
        }}>
          Video archivee
        </h2>
        <p style={{
          fontFamily: T.body, fontSize: 14, color: MUTED, margin: '0 0 8px', lineHeight: 1.5,
        }}>
          La video de ce match a depasse la periode de conservation de {VIDEO_RETENTION_DAYS} jours
          {expiresAt && ` (expiree le ${expiresAt.toLocaleDateString('fr-FR')})`}.
        </p>
        <p style={{
          fontFamily: T.body, fontSize: 13, color: MUTED, margin: 0, lineHeight: 1.5,
        }}>
          Tes annotations et moments restent accessibles dans la bibliotheque.
        </p>
      </div>
    </DashboardLayout>
  )
}

// ─── Verrou de navigation pendant le mode annotation ─────────────────────
// L'overlay affiche "frame figee" : tant qu'il est actif, plus rien ne doit
// pouvoir deplacer currentTime. Sinon le dessin se retrouve pose sur une image
// qui n'est pas la sienne, et une sauvegarde l'ecrit au NOUVEAU timecode —
// donnee fausse, sans erreur ni signal.
//
// Deux mecanismes complementaires, pas un seul :
//   - `inert` coupe le focus, le clavier et les evenements. React 18 ne connait
//     pas l'attribut en booleen, il faut le passer en chaine vide.
//   - `pointer-events: none` couvre la souris et sert de repli si le navigateur
//     ignore `inert`. C'est le chemin par lequel le bug a ete constate.
// L'opacite reduite fait comprendre le verrou sans texte ; le contenu reste
// lisible pour se reperer, et le curseur cesse d'indiquer la cliquabilite
// (pointer-events: none => le curseur de l'element n'est plus applique).
function NavLock({ locked, style, children }) {
  return (
    <div
      style={{
        ...style,
        transition: 'opacity .15s',
        // 0.7 et non moins : l'opacite est portee par le conteneur, donc elle
        // se compose avec les couleurs deja claires a l'interieur. Le gris des
        // listes est rgba(26,25,22,0.45) ; a 0.55 de conteneur il tombe vers
        // 0.25 d'alpha effectif sur fond blanc, donc illisible. Une zone
        // verrouillee doit rester lisible pour se reperer.
        ...(locked ? { pointerEvents: 'none', opacity: 0.7 } : null),
      }}
      {...(locked ? { inert: '' } : {})}
    >
      {children}
    </div>
  )
}

function StudioLoading() {
  return (
    <DashboardLayout>
      <style>{globalStyles}</style>
      <div style={{
        minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: MONO, fontSize: 10, letterSpacing: '.2em',
        textTransform: 'uppercase', color: MUTED,
      }}>
        Chargement du studio...
      </div>
    </DashboardLayout>
  )
}

function StudioError({ matchId, message }) {
  return (
    <DashboardLayout>
      <style>{globalStyles}</style>
      <Link to={`/dashboard/matches/${matchId}`} style={{
        display: 'flex', alignItems: 'center', gap: 8, fontFamily: MONO,
        fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase',
        color: MUTED, marginBottom: 20, textDecoration: 'none',
      }}>
        <ArrowLeft size={14} /> Retour au match
      </Link>
      <div style={{
        background: '#fff', border: `1px solid ${RULE}`, borderTop: `2px solid ${RED}`,
        padding: 40, textAlign: 'center', maxWidth: 560, margin: '0 auto',
      }}>
        <AlertCircle size={32} color={RED} style={{ margin: '0 auto 16px' }} />
        <h2 style={{
          fontFamily: DISP, fontSize: 22, textTransform: 'uppercase',
          color: INK, margin: '0 0 12px',
        }}>
          Erreur
        </h2>
        <p style={{
          fontFamily: T.body, fontSize: 14, color: MUTED, margin: 0,
        }}>
          {message || 'Impossible de charger ce match.'}
        </p>
      </div>
    </DashboardLayout>
  )
}

// ─── Page principale ──────────────────────────────────────────────────
export default function VideoStudio() {
  const { matchId } = useParams()
  const { user } = useAuth()
  const playerRef = useRef(null)
  // Anti double-tap sur "Marquer un moment" : sur mobile, deux taps
  // rapproches ouvraient deux modales. Garde 500ms via ref (pas de
  // re-render).
  const isMarkingRef = useRef(false)

  const [match, setMatch]       = useState(null)
  const [loading, setLoading]   = useState(true)
  const [error, setError]       = useState(null)
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768)
  const [isNarrow, setIsNarrow] = useState(() => typeof window !== 'undefined' && window.innerWidth < 480)

  const [moments, setMoments]           = useState([])
  const [loadingMoments, setLoadingMoments] = useState(false)
  const [editingMoment, setEditingMoment]   = useState(null)
  const [trimMode, setTrimMode]             = useState(false)
  const [annotationMode, setAnnotationMode] = useState(false)
  const [annotationTimecode, setAnnotationTimecode] = useState(0)
  // Moment cible du mode annotation. null = annotation d'une frame libre depuis
  // le CTA du header : le moment sera cree a l'enregistrement.
  const [annotationMoment, setAnnotationMoment] = useState(null)
  // En ref et non en state : sert uniquement au garde-fou de sortie, aucun
  // rendu n'en depend.
  const annotationDirtyRef = useRef(false)

  // ── Mode lecture des annotations ──────────────────────────────────────
  // Passif par defaut : hors de ce mode, la lecture video est strictement
  // identique a avant, aucune annotation affichee, aucun arret.
  const [readingMode, setReadingMode]   = useState(false)
  const [activePoint, setActivePoint]   = useState(null)   // point d'arret affiche
  const [countdown, setCountdown]       = useState(null)   // secondes restantes, null = fige
  // Position de la tete de lecture au tick precedent. En ref : la boucle de
  // detection tourne a 60 Hz, un setState par frame saturerait le rendu.
  // `lastTimeRef` sert encore a stopOnPoint · la boucle, ses miroirs de refs,
  // son garde-fou de fin et son suivi de scrub vivent desormais dans
  // useAnnotationStops, partage avec la page de seance publique.
  const lastTimeRef  = useRef(0)
  const countdownRef = useRef(null)
  const [clipTimecode, setClipTimecode]     = useState(0)
  const [panelOpen, setPanelOpen]           = useState(() => {
    try { return localStorage.getItem('clipsPanelOpen') === 'true' } catch { return false }
  })
  const [creatingMoment, setCreatingMoment] = useState(false)
  const [markTimecode, setMarkTimecode]     = useState(0)

  // URL signee S3 pour la lecture video (1h de validite, refetch auto si expire)
  const [videoUrl, setVideoUrl] = useState(null)
  // Mi-temps active (1|2) quand le match a ete decoupe ; null = legacy full video
  const [activeHalf, setActiveHalf] = useState(1)
  const [hasHalves, setHasHalves] = useState(false)

  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration]       = useState(0)

  const [toast, setToast] = useState(null)
  const showToast = (msg, type = 'success') => {
    setToast({ msg, type })
    setTimeout(() => setToast(null), 3000)
  }

  // Compteur clips FREE (cumulatif sur 30j d'essai). Pour les autres plans : null.
  // Synchronise via /api/moments/stats/ apres chaque extraction reussie.
  const [freeClipsUsed, setFreeClipsUsed] = useState(0)

  // Plafond anti-derive · 30 clips par match, tous coachs confondus. Le
  // compteur vient du serveur, seul a savoir ce que les autres coachs du club
  // ont deja extrait sur CE match. `null` tant qu'il n'a pas repondu · afficher
  // 0/30 avant la reponse annoncerait un plafond intact a un coach qui y est.
  const [clipsDuMatch, setClipsDuMatch] = useState(null)
  const [plafondClipsMatch, setPlafondClipsMatch] = useState(null)

  const access = hasStudioAccess(user, freeClipsUsed)
  const accessAllowed = access.allowed
  const auPlafondDuMatch = (
    clipsDuMatch !== null && plafondClipsMatch !== null && clipsDuMatch >= plafondClipsMatch
  )

  const refreshClipsCounter = useCallback(async () => {
    if (!user) return
    try {
      // `match_id` · le studio ouvre toujours sur un match donne, et c'est ce
      // match qui porte le plafond.
      const r = await fetch(`${API}/moments/stats/?match_id=${encodeURIComponent(matchId)}`, { headers: authHeaders() })
      if (!r.ok) return
      const data = await r.json()
      if (typeof data.free_clips_used === 'number') {
        setFreeClipsUsed(data.free_clips_used)
      }
      if (typeof data.match_clips_used === 'number') {
        setClipsDuMatch(data.match_clips_used)
      }
      if (typeof data.match_clips_limit === 'number') {
        setPlafondClipsMatch(data.match_clips_limit)
      }
    } catch (e) { /* best-effort */ }
  }, [user, matchId])

  useEffect(() => {
    if (accessAllowed) refreshClipsCounter()
  }, [accessAllowed, refreshClipsCounter])

  useEffect(() => {
    const check = () => {
      setIsMobile(window.innerWidth < 768)
      setIsNarrow(window.innerWidth < 480)
    }
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  // Fetch match
  useEffect(() => {
    if (!accessAllowed) {
      setLoading(false)
      return
    }
    fetch(`${API}/matches/${matchId}/`, { headers: authHeaders() })
      .then(r => {
        if (!r.ok) throw new Error('Match introuvable')
        return r.json()
      })
      .then(data => { setMatch(data); setLoading(false) })
      .catch(e => { setError(e.message); setLoading(false) })
  }, [matchId, accessAllowed])

  // Detecte si le match a des mi-temps
  useEffect(() => {
    if (!match) return
    const reallyHasHalves = Boolean(
      (match.video_url_mt1 && match.video_url_mt2) || match.has_halves
    )
    setHasHalves(reallyHasHalves)
    if (reallyHasHalves) setActiveHalf((h) => (h === 1 || h === 2 ? h : 1))
  }, [match])

  // Fetch URL signee fraiche pour la mi-temps active (ou legacy).
  useEffect(() => {
    const playable = match && (
      match.video_url ||
      match.video_url_mt1 ||
      match.video_url_mt2 ||
      match.has_video
    )
    if (!playable) return

    let cancelled = false

    const fetchSignedUrl = async () => {
      try {
        const qs = hasHalves ? `?half=${activeHalf}` : ''
        const r = await fetch(`${API}/matches/${matchId}/video-url${qs}`, {
          headers: authHeaders()
        })
        if (!r.ok) throw new Error('Impossible de generer URL video')
        const data = await r.json()
        if (!cancelled && data.has_video && data.video_url) {
          setVideoUrl(data.video_url)
          if (data.has_halves) setHasHalves(true)
        }
      } catch (e) {
        console.error('fetchSignedUrl error:', e)
      }
    }

    fetchSignedUrl()
    const interval = setInterval(fetchSignedUrl, 50 * 60 * 1000)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [match, matchId, activeHalf, hasHalves])

  const fetchMoments = useCallback(async () => {
    if (!accessAllowed) return
    setLoadingMoments(true)
    try {
      const r = await fetch(`${API}/matches/${matchId}/moments/`, { headers: authHeaders() })
      if (!r.ok) throw new Error('Erreur de chargement des moments')
      const data = await r.json()
      setMoments(data.moments || [])
    } catch (e) {
      console.error('fetchMoments error:', e)
    } finally {
      setLoadingMoments(false)
    }
  }, [matchId, accessAllowed])

  useEffect(() => {
    if (match) fetchMoments()
  }, [match, fetchMoments])

  // Actions
  const handleMarkClick = () => {
    if (isMarkingRef.current) return
    isMarkingRef.current = true
    // Meme raison : la modale se poserait par-dessus une lecture qui continue
    // de declencher des arrets derriere elle.
    if (readingMode) exitReadingMode()
    const t = playerRef.current?.getCurrentTime() ?? 0
    playerRef.current?.pause()
    setMarkTimecode(t)
    setCreatingMoment(true)
    setTimeout(() => { isMarkingRef.current = false }, 500)
  }

  const handleEditMoment = (moment) => {
    setEditingMoment(moment)
  }

  const handleCloseModal = () => {
    setCreatingMoment(false)
    setEditingMoment(null)
  }

  const handleCreate = async (payload) => {
    const body = {
      ...payload,
      ...(hasHalves ? { half: activeHalf } : {}),
    }
    const r = await fetch(`${API}/matches/${matchId}/moments/`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(body),
    })
    if (!r.ok) {
      const err = await r.json().catch(() => ({}))
      throw new Error(err.detail || 'Erreur lors de la creation')
    }
    const created = await r.json()
    setMoments(prev => [...prev, created].sort((a, b) => a.timecode_start - b.timecode_start))
    showToast('Moment marque')
  }

  const handleUpdate = async (momentId, payload) => {
    const r = await fetch(`${API}/moments/${momentId}/`, {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify(payload),
    })
    if (!r.ok) {
      const err = await r.json().catch(() => ({}))
      throw new Error(err.detail || 'Erreur lors de la mise a jour')
    }
    const updated = await r.json()
    setMoments(prev => prev.map(m => m.id === momentId ? updated : m)
      .sort((a, b) => a.timecode_start - b.timecode_start))
    showToast('Moment mis a jour')
  }

  const handleDelete = async (momentId) => {
    const r = await fetch(`${API}/moments/${momentId}/`, {
      method: 'DELETE',
      headers: authHeaders(),
    })
    if (!r.ok && r.status !== 204) {
      const err = await r.json().catch(() => ({}))
      throw new Error(err.detail || 'Erreur lors de la suppression')
    }
    setMoments(prev => prev.filter(m => m.id !== momentId))
    showToast('Moment supprime')
  }

  // Flèche dorée : bascule un moment entre "Mes moments" et "Pour les
  // joueurs". En passant a droite, is_public=true (Public par defaut).
  const handleToggleForCard = async (m) => {
    try {
      await handleUpdate(
        m.id,
        m.for_card ? { for_card: false } : { for_card: true, is_public: true },
      )
    } catch (e) {
      showToast(e.message || 'Erreur', 'error')
    }
  }

  // Toggle Public/Privé sur un moment "Pour les joueurs".
  // is_public !== false => actuellement Public ; on inverse.
  const handleTogglePublic = async (m) => {
    const makePublic = m.is_public === false
    try {
      await handleUpdate(m.id, { is_public: makePublic })
    } catch (e) {
      showToast(e.message || 'Erreur', 'error')
    }
  }

  const handleSeek = (timecode) => {
    playerRef.current?.seekTo(timecode)
    playerRef.current?.pause()
  }

  // Toggle du mode decoupe inline. A l'ouverture : capture le timecode exact
  // et met la video en pause pour figer le contexte de la selection.
  const handleToggleTrim = () => {
    if (trimMode) { setTrimMode(false); return }
    if (annotationMode) return   // pas de decoupe pendant l'annotation
    if (readingMode) exitReadingMode()   // sortie silencieuse : rien a perdre
    const t = playerRef.current?.getCurrentTime() ?? 0
    playerRef.current?.pause()
    setClipTimecode(t)
    setTrimMode(true)
  }

  // Points d'arret annotes du match. Source unique : le compteur du bouton, les
  // marqueurs de timeline, la mecanique d'arret du mode lecture et la
  // reouverture d'une annotation existante lisent tous celui-ci. Pas de calcul
  // parallele qui pourrait diverger.
  const annotationPoints = useMemo(() => collectAnnotationPoints(moments), [moments])

  // Sortie du mode annotation. Point de passage unique : le CTA du header, la
  // touche Echap de l'overlay et le bouton Quitter partagent le meme garde-fou,
  // sinon on retombe sur le trou de la PR #190 (quitter sans confirmation
  // perdait le dessin en silence).
  const closeAnnotationMode = useCallback(() => {
    if (annotationDirtyRef.current) {
      const ok = window.confirm(
        'Quitter sans enregistrer ? Les annotations de cette frame seront perdues.',
      )
      if (!ok) return
    }
    annotationDirtyRef.current = false
    setAnnotationMode(false)
    setAnnotationMoment(null)
  }, [])

  // Toggle du mode annotation depuis le header : fige la frame courante
  // (pause + timecode) et ouvre l'annotation DEJA POSEE sur cette image quand
  // il y en a une.
  //
  // C'est le seul chemin que le coach emprunte, et il repartait d'une toile
  // vierge en toutes circonstances : revenir sur sa frame pour corriger un
  // libelle produisait un moment de plus, l'original restant intact et
  // invisible. La prod en porte la trace — trois moments sur la meme phase en
  // neuf minutes, jamais aucun `updated_at` different de `created_at`.
  //
  // On se recale sur le `t` du jeu trouve avant d'ouvrir : sans ce seek, la
  // frame montree n'est pas celle qui a ete annotee et le dessin est decale.
  // Meme raison qu'au §stopOnPoint du mode lecture.
  const handleToggleAnnotation = () => {
    if (annotationMode) { closeAnnotationMode(); return }
    if (trimMode) return   // pas d'annotation pendant la decoupe
    if (readingMode) exitReadingMode()   // sortie silencieuse : rien a perdre
    const t = playerRef.current?.getCurrentTime() ?? 0
    const point = findAnnotationPointAt(annotationPoints, t)
    const target = point ? moments.find(m => m.id === point.momentId) : null
    playerRef.current?.pause()
    if (target) {
      playerRef.current?.seekTo(point.t)
      setAnnotationTimecode(point.t)
      setAnnotationMoment(target)
    } else {
      setAnnotationTimecode(t)
      setAnnotationMoment(null)
    }
    annotationDirtyRef.current = false
    setAnnotationMode(true)
  }

  // Annoter un moment existant, depuis le bouton "Annoter la frame" de la
  // modale : on ferme la modale, on se cale sur la frame du moment, et on ouvre
  // l'overlay lie a ce moment (PATCH a l'enregistrement).
  const handleAnnotateMoment = (m) => {
    if (!m) return
    setEditingMoment(null)
    setCreatingMoment(false)
    setTrimMode(false)
    playerRef.current?.seekTo(m.timecode_start)
    playerRef.current?.pause()
    setAnnotationTimecode(m.timecode_start)
    setAnnotationMoment(m)
    annotationDirtyRef.current = false
    setAnnotationMode(true)
  }

  // ── Mode lecture : points, detection, enchainement ────────────────────

  const clearCountdown = useCallback(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current)
      countdownRef.current = null
    }
    setCountdown(null)
  }, [])

  const exitReadingMode = useCallback(() => {
    clearCountdown()
    setReadingMode(false)
    setActivePoint(null)
  }, [clearCountdown])

  // Arret sur un point : on pose currentTime EXPLICITEMENT avant d'afficher.
  // Sans ce recalage, la frame montree n'est pas celle qui a ete annotee et le
  // dessin est decale de quelques images.
  const stopOnPoint = useCallback((point, { withCountdown }) => {
    clearCountdown()
    playerRef.current?.pause()
    playerRef.current?.seekTo(point.t)
    lastTimeRef.current = point.t
    setActivePoint(point)
    if (!withCountdown) return
    setCountdown(READING_HOLD_SECONDS)
    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev === null) return null
        if (prev > 1) return prev - 1
        // Fin du decompte : on relance et on enchaine.
        clearInterval(countdownRef.current)
        countdownRef.current = null
        setActivePoint(null)
        playerRef.current?.play()
        return null
      })
    }, 1000)
  }, [clearCountdown])

  const startReadingMode = useCallback(() => {
    if (!annotationPoints.length) return
    setTrimMode(false)
    setReadingMode(true)
    stopOnPoint(annotationPoints[0], { withCountdown: true })
  }, [annotationPoints, stopOnPoint])

  // Saut manuel d'un point a l'autre. Pas de decompte : le coach a saute
  // deliberement, il repart quand il veut.
  const goToAdjacentPoint = useCallback((delta) => {
    if (!annotationPoints.length) return
    const ref = activePoint ? activePoint.t : (playerRef.current?.getCurrentTime() ?? 0)
    const idx = annotationPoints.findIndex(p => Math.abs(p.t - ref) < 0.001)
    let next
    if (idx >= 0) {
      next = annotationPoints[idx + delta]
    } else {
      next = delta > 0
        ? annotationPoints.find(p => p.t > ref)
        : [...annotationPoints].reverse().find(p => p.t < ref)
    }
    if (!next) return
    stopOnPoint(next, { withCountdown: false })
  }, [annotationPoints, activePoint, stopOnPoint])

  // Fige l'image en cours en annulant le decompte, ou relance depuis un arret.
  const toggleHold = useCallback(() => {
    if (countdown !== null) { clearCountdown(); return }
    setActivePoint(null)
    clearCountdown()
    playerRef.current?.play()
  }, [countdown, clearCountdown])

  // Arret automatique sur les frames annotees. Le mecanisme vit dans
  // useAnnotationStops, partage avec la page de seance publique · c'est la meme
  // regle metier, et deux copies auraient diverge.
  //
  // Le Studio ne passe PAS de segments · il lit la video entiere et s'arrete
  // sur les points. La page publique, elle, lit des segments derives.
  useAnnotationStops(playerRef, {
    active: readingMode,
    points: annotationPoints,
    heldPoint: activePoint,
    onStop: useCallback((point) => stopOnPoint(point, { withCountdown: true }), [stopOnPoint]),
    onRelease: useCallback(() => { clearCountdown(); setActivePoint(null) }, [clearCountdown]),
    onFinished: useCallback(() => exitReadingMode(), [exitReadingMode]),
  })

  useEffect(() => () => clearCountdown(), [clearCountdown])

  // `saved` est le moment renvoye par le backend (cree ou mis a jour). Le
  // message nomme le timecode et le moment porteur : le coach comprend en une
  // fois que son dessin est range dans un moment, sans documentation.
  const handleAnnotationSuccess = (saved) => {
    const wasNew = !annotationMoment
    const t = Number.isFinite(saved?.timecode_start) ? saved.timecode_start : annotationTimecode
    annotationDirtyRef.current = false
    setAnnotationMode(false)
    setAnnotationMoment(null)
    fetchMoments()
    showToast(wasNew
      ? `Moment créé à ${formatClock(t)} avec ton annotation`
      : `Annotation enregistrée sur le moment de ${formatClock(t)}`)
  }

  // Clip cree avec succes : recharge la liste (le nouveau clip apparait dans
  // ClipsPanel), rafraichit le compteur FREE, sort du mode decoupe.
  // ─── Lien de seance ───────────────────────────────────────────────────
  // Une selection de clips devient une URL publique, a projeter en seance et a
  // envoyer aux joueurs. Le lien est VIVANT · rien n'est copie, les annotations
  // sont relues a chaque ouverture. Corriger un dessin apres l'envoi suffit.
  const [sharing, setSharing] = useState(false)
  const [lienSeance, setLienSeance] = useState(null)
  const [partageOuvert, setPartageOuvert] = useState(false)

  // On partage des GROUPES nommes · la partition est figee a la creation, c'est
  // ce qui garantit qu'un titre ne se deplace pas si la fenetre de fusion evolue.
  //
  // `token` non nul · on MODIFIE une seance deja distribuee. Le lien ne change
  // pas — meme URL, meme expiration — c'est tout l'interet, celui qui est chez
  // les joueurs reste valable et il n'y a rien a renvoyer.
  const handleShareClips = async (groupes, token = null) => {
    if (!groupes?.length || sharing) return
    setSharing(true)
    try {
      const r = await fetch(
        token ? `${API}/clip-shares/${token}/` : `${API}/matches/${matchId}/clip-shares/`, {
          method: token ? 'PATCH' : 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ groupes }),
        })
      const data = await r.json().catch(() => ({}))
      if (!r.ok) {
        // Le detail du backend est un message produit (« Aucun clip a
        // partager… »), pas un code technique · on le montre tel quel.
        const detail = typeof data?.detail === 'string' ? data.detail : null
        throw new Error(detail || 'Le lien n\'a pas pu être créé.')
      }
      setPartageOuvert(false)
      setLienSeance({
        misAJour: !!token,
        url: `${window.location.origin}/seance/${data.token}`,
        expiresAt: data.expires_at,
        arrets: data.arrets,
        groupes: data.groupes,
        ignores: data.ignores,
      })
    } catch (e) {
      showToast(e?.message || 'Le lien n\'a pas pu être créé', 'error')
    } finally {
      setSharing(false)
    }
  }

  const handleTrimSuccess = () => {
    fetchMoments()
    refreshClipsCounter()
    setTrimMode(false)
    showToast('Clip genere')
  }

  // Ouvre/ferme le panneau clips + persiste la preference (desktop).
  const handleTogglePanel = () => {
    setPanelOpen(prev => {
      const next = !prev
      try { localStorage.setItem('clipsPanelOpen', String(next)) } catch { /* noop */ }
      return next
    })
  }

  // Clic sur un clip du panneau : lecture dans le player principal a partir
  // du timecode de debut du moment.
  const handleSelectClip = (m) => {
    if (hasHalves && (m.half === 1 || m.half === 2) && m.half !== activeHalf) {
      setActiveHalf(m.half)
      // seek apres chargement de la nouvelle URL (effet video-url)
      setTimeout(() => {
        playerRef.current?.seekTo(m.timecode_start)
        playerRef.current?.play()
      }, 400)
      return
    }
    playerRef.current?.seekTo(m.timecode_start)
    playerRef.current?.play()
  }

  // Telechargement du MP4 via le flow token 2 etapes (le GET ne peut pas
  // porter l'header Authorization).
  const handleDownloadClip = async (m) => {
    try {
      const r = await fetch(`${API}/moments/${m.id}/clip-download-token/`, {
        method: 'POST', headers: authHeaders(),
      })
      if (!r.ok) throw new Error('Impossible de preparer le telechargement')
      const { token } = await r.json()
      if (!token) throw new Error('Token de telechargement manquant')
      window.location.href = `${API}/moments/${m.id}/clip-download/?dt=${encodeURIComponent(token)}`
    } catch (e) {
      showToast(e.message || 'Erreur lors du telechargement', 'error')
    }
  }

  // ── Etats ────────────────────────────────────────────────────────────
  if (!accessAllowed) return <StudioLocked reason={access.reason} />
  if (loading) return <StudioLoading />
  if (error) return <StudioError matchId={matchId} message={error} />
  if (!match) return <StudioError matchId={matchId} message="Match introuvable." />
  const matchHasVideo = match.has_video || match.video_url || (match.video_url_mt1 && match.video_url_mt2)
  if (!matchHasVideo) return <NoVideoState matchId={matchId} />

  const expiration = computeVideoExpiration(match.uploaded_at)
  if (expiration?.isExpired) {
    return <VideoExpiredState matchId={matchId} expiresAt={expiration.expiresAt} />
  }

  const lineup    = extractLineup(match)
  const matchDate = match.date
    ? new Date(match.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
    : ''

  const readingBtn = {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 38, height: 38,
    background: 'transparent', color: CREAM,
    border: '1px solid rgba(245,242,235,0.3)',
    cursor: 'pointer', transition: 'all .15s',
  }

  const momentsForCard = moments.filter(m => m.for_card)
  const momentsPrivate = moments.filter(m => !m.for_card)
  const hasLineup      = lineup.length > 0
  // Timeline : moments de la mi-temps active (legacy half=null → MT1)
  const timelineMoments = hasHalves
    ? moments.filter((m) => m.half === activeHalf || (m.half == null && activeHalf === 1))
    : moments

  return (
    <DashboardLayout>
      <style>{globalStyles}{`
        @keyframes fadeUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes toastIn{from{opacity:0;transform:translateY(-10px)}to{opacity:1;transform:translateY(0)}}
      `}</style>

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 24, right: 24, zIndex: 10000,
          padding: '12px 24px',
          background: toast.type === 'error' ? RED : GOLD,
          color: toast.type === 'error' ? '#fff' : D,
          fontFamily: MONO, fontSize: 10, letterSpacing: '.08em',
          textTransform: 'uppercase', fontWeight: 700,
          animation: 'toastIn .25s ease-out',
        }}>
          {toast.msg}
        </div>
      )}

      {/* Selection des annotations a partager. Ouverte depuis le header · c'est
          le seul point d'entree, et il est visible des qu'une annotation existe. */}
      {partageOuvert && (
        <PartageAnnotationsModal
          matchId={matchId}
          sharing={sharing}
          onShare={handleShareClips}
          onClose={() => setPartageOuvert(false)}
        />
      )}

      {/* Lien de seance genere. Une modale et non un toast · le coach doit
          pouvoir copier l'URL, et un message qui disparait au bout de 3 s
          emporterait ce qu'il vient de fabriquer. */}
      {lienSeance && (
        <div
          role="dialog"
          aria-label="Lien de séance"
          onClick={() => setLienSeance(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(26,25,22,0.72)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
          }}
        >
          <div onClick={e => e.stopPropagation()} style={{
            background: CREAM, borderRadius: 10, padding: 22, maxWidth: 460, width: '100%',
          }}>
            <h2 style={{
              fontFamily: DISP, fontSize: 22, textTransform: 'uppercase',
              color: D, margin: '0 0 6px', lineHeight: 1,
            }}>
              {lienSeance.misAJour ? 'Séance mise à jour' : 'Lien de séance'}
            </h2>
            <p style={{ fontFamily: T.body, fontSize: 13, color: 'rgba(26,25,22,0.65)', margin: '0 0 14px', lineHeight: 1.5 }}>
              {lienSeance.groupes} action{lienSeance.groupes > 1 ? 's' : ''} ·{' '}
              {lienSeance.arrets} arrêt{lienSeance.arrets > 1 ? 's' : ''} · expire le{' '}
              {new Date(lienSeance.expiresAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}.
              {lienSeance.ignores > 0 && ` ${lienSeance.ignores} moment sans annotation n'y figure pas.`}
              {' '}{lienSeance.misAJour
                ? "Le lien n'a pas changé · tes joueurs verront la mise à jour en rechargeant."
                : 'Tes annotations restent modifiables · le lien montre toujours la dernière version.'}
            </p>
            <input
              readOnly
              value={lienSeance.url}
              onFocus={e => e.target.select()}
              style={{
                width: '100%', fontFamily: MONO, fontSize: 12, padding: '10px 12px',
                border: '1px solid rgba(26,25,22,0.2)', borderRadius: 6,
                background: '#fff', color: D, marginBottom: 12,
              }}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setLienSeance(null)}
                style={{
                  fontFamily: T.body, fontSize: 13, minHeight: 40, padding: '0 14px',
                  background: 'none', border: '1px solid rgba(26,25,22,0.2)',
                  borderRadius: 6, cursor: 'pointer', color: D,
                }}
              >
                Fermer
              </button>
              <button
                onClick={() => {
                  navigator.clipboard?.writeText(lienSeance.url)
                    .then(() => showToast('Lien copié'))
                    .catch(() => showToast('Copie impossible, sélectionne le lien', 'error'))
                }}
                style={{
                  fontFamily: T.body, fontSize: 13, fontWeight: 600, minHeight: 40, padding: '0 16px',
                  background: GOLD, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer',
                }}
              >
                Copier le lien
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal creation */}
      {creatingMoment && (
        <MomentMarker
          timecode={markTimecode}
          players={lineup}
          matchId={matchId}
          onSave={handleCreate}
          onClose={handleCloseModal}
        />
      )}

      {/* Modal edition */}
      {editingMoment && (
        <MomentMarker
          moment={editingMoment}
          players={lineup}
          matchId={matchId}
          onSave={(payload) => handleUpdate(editingMoment.id, payload)}
          onDelete={() => handleDelete(editingMoment.id)}
          onAnnotate={handleAnnotateMoment}
          onClose={handleCloseModal}
        />
      )}

      {/* Retour */}
      <Link to={`/dashboard/matches/${matchId}`} style={{
        display: 'flex', alignItems: 'center', gap: 8, fontFamily: MONO,
        fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase',
        color: MUTED, marginBottom: 20, textDecoration: 'none',
      }}>
        <ArrowLeft size={14} /> Retour au match
      </Link>

      {/* Header sombre */}
      <div style={{
        background: D, animation: 'fadeUp .35s ease',
        padding: '20px 24px', position: 'relative', overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.03,
          backgroundImage: 'linear-gradient(rgba(79,142,247,1) 1px, transparent 1px), linear-gradient(90deg, rgba(79,142,247,1) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }} />
        <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ width: 14, height: 1, background: GOLD, display: 'inline-block' }} />
              <span style={{
                fontFamily: MONO, fontSize: 10, letterSpacing: '.18em',
                textTransform: 'uppercase', color: GOLD,
              }}>
                Studio video
              </span>
            </div>
            <h1 style={{
              fontFamily: DISP, fontSize: 28, textTransform: 'uppercase',
              lineHeight: .9, letterSpacing: '.01em', color: CREAM, margin: 0,
            }}>
              vs {match.opponent}
            </h1>
            <div style={{
              fontFamily: MONO, fontSize: 10, letterSpacing: '.1em',
              textTransform: 'uppercase', color: MUTED_DARK, marginTop: 6,
            }}>
              {match.category} · {matchDate}
            </div>
          </div>

          <div style={{
            display: 'flex', gap: 10, flexWrap: 'wrap',
            flexDirection: isNarrow ? 'column' : 'row',
            alignItems: isNarrow ? 'stretch' : 'center',
          }}>
            {/* Plafond anti-derive · clips extraits SUR CE MATCH, tous coachs
                du club confondus. C'est le cout de traitement de cette video
                qui est borne, pas l'activite d'un compte — d'ou « sur ce
                match » dans le libelle, sans quoi un coach lirait le compteur
                comme un quota personnel.
                Rendu des que le serveur a repondu, pour tous les plans qui ont
                acces au studio, membres de club inclus. */}
            {clipsDuMatch !== null && plafondClipsMatch !== null && (
              <div
                title={auPlafondDuMatch
                  ? `Limite de ${plafondClipsMatch} clips atteinte sur ce match. Les clips deja extraits restent disponibles.`
                  : `${clipsDuMatch}/${plafondClipsMatch} clips extraits sur ce match, tous coachs du club confondus`}
                style={{
                  fontFamily: MONO, fontSize: 9, letterSpacing: '.1em',
                  textTransform: 'uppercase',
                  color: auPlafondDuMatch ? ORANGE : GOLD,
                  border: `1px solid ${auPlafondDuMatch ? ORANGE : 'rgba(79,142,247,0.4)'}`,
                  padding: '6px 12px',
                  background: auPlafondDuMatch ? 'rgba(245,158,11,0.08)' : 'rgba(79,142,247,0.08)',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                <Scissors size={11} />
                {clipsDuMatch}/{plafondClipsMatch} clips sur ce match
              </div>
            )}

            {expiration && !expiration.isExpired && (
              <div style={{
                fontFamily: MONO, fontSize: 9, letterSpacing: '.1em',
                textTransform: 'uppercase',
                color: expiration.daysLeft <= 7 ? ORANGE : MUTED_DARK,
                border: `1px solid ${expiration.daysLeft <= 7 ? ORANGE : 'rgba(245,242,235,0.15)'}`,
                padding: '6px 12px',
                background: expiration.daysLeft <= 7 ? 'rgba(245,158,11,0.08)' : 'transparent',
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <Calendar size={11} />
                {expiration.daysLeft > 0
                  ? `Expire dans ${expiration.daysLeft}j`
                  : 'Expire aujourd\'hui'}
              </div>
            )}

            {/* Selecteur MT1 / MT2 */}
            {hasHalves && (
              <div
                role="group"
                aria-label="Mi-temps"
                style={{ display: 'flex', border: `1px solid rgba(79,142,247,0.4)` }}
              >
                {[1, 2].map((h) => (
                  <button
                    key={h}
                    type="button"
                    onClick={() => {
                      if (h === activeHalf) return
                      setTrimMode(false)
                      setCurrentTime(0)
                      setVideoUrl(null)
                      setActiveHalf(h)
                    }}
                    aria-pressed={activeHalf === h}
                    style={{
                      padding: '9px 14px',
                      background: activeHalf === h ? GOLD : 'transparent',
                      color: activeHalf === h ? D : GOLD,
                      border: 'none',
                      fontFamily: MONO, fontSize: 11, letterSpacing: '.1em',
                      textTransform: 'uppercase', fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    MT{h}
                  </button>
                ))}
              </div>
            )}

            {/* Lecture des annotations. N'existe pas si le match n'en porte
                aucune : pas de bouton grise, pas de compteur a zero. */}
            {annotationPoints.length > 0 && !readingMode && (
              <button
                onClick={startReadingMode}
                disabled={annotationMode}
                aria-label={`Lire les annotations (${annotationPoints.length})`}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  padding: '9px 16px',
                  background: 'transparent', color: CREAM,
                  border: '1px solid rgba(245,242,235,0.3)',
                  fontFamily: MONO, fontSize: 11, letterSpacing: '.1em',
                  textTransform: 'uppercase', fontWeight: 700,
                  cursor: annotationMode ? 'default' : 'pointer',
                  opacity: annotationMode ? 0.4 : 1,
                  transition: 'all .15s',
                }}
                onMouseEnter={e => { if (!annotationMode) e.currentTarget.style.borderColor = CREAM }}
                onMouseLeave={e => { if (!annotationMode) e.currentTarget.style.borderColor = 'rgba(245,242,235,0.3)' }}
              >
                <PlayCircle size={13} /> Lire les annotations ({annotationPoints.length})
              </button>
            )}

            {/* Partage. Meme condition d'existence que la lecture · une
                annotation au moins. Il vit ICI, dans le header, aux cotes des
                autres actions du coach — et non au fond d'un panneau replie
                derriere une case sans libelle, ou personne ne l'a trouve. */}
            {annotationPoints.length > 0 && !readingMode && (
              <button
                onClick={() => setPartageOuvert(true)}
                disabled={annotationMode}
                aria-label="Partager des annotations"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  padding: '9px 16px',
                  background: 'transparent', color: CREAM,
                  border: '1px solid rgba(245,242,235,0.3)',
                  fontFamily: MONO, fontSize: 11, letterSpacing: '.1em',
                  textTransform: 'uppercase', fontWeight: 700,
                  cursor: annotationMode ? 'default' : 'pointer',
                  opacity: annotationMode ? 0.4 : 1,
                  transition: 'all .15s',
                }}
                onMouseEnter={e => { if (!annotationMode) e.currentTarget.style.borderColor = CREAM }}
                onMouseLeave={e => { if (!annotationMode) e.currentTarget.style.borderColor = 'rgba(245,242,235,0.3)' }}
              >
                <Share2 size={13} /> Partager des annotations
              </button>
            )}

            {/* Commandes du mode lecture : n'existent que dans ce mode. */}
            {readingMode && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <button
                  onClick={() => goToAdjacentPoint(-1)}
                  aria-label="Point précédent"
                  style={readingBtn}
                >
                  <SkipBack size={13} />
                </button>
                <button
                  onClick={() => goToAdjacentPoint(1)}
                  aria-label="Point suivant"
                  style={readingBtn}
                >
                  <SkipForward size={13} />
                </button>
                <button
                  onClick={exitReadingMode}
                  aria-label="Quitter la lecture"
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    padding: '9px 16px',
                    background: RED_CTA, color: '#fff', border: 'none',
                    fontFamily: MONO, fontSize: 11, letterSpacing: '.1em',
                    textTransform: 'uppercase', fontWeight: 700,
                    cursor: 'pointer', transition: 'opacity .15s',
                  }}
                >
                  <X size={13} /> Quitter la lecture
                </button>
              </div>
            )}

            {/* CTA principal : bascule le mode decoupe inline */}
            <button
              onClick={handleToggleTrim}
              // handleToggleTrim refusait deja d'ouvrir pendant l'annotation,
              // mais le bouton restait cliquable sans aucun retour. Un bouton
              // qui ne fait rien est pire qu'un bouton desactive.
              disabled={annotationMode}
              aria-label={trimMode ? 'Annuler le découpage' : 'Découper un clip'}
              aria-pressed={trimMode}
              style={{
                position: 'relative',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                padding: '9px 16px',
                background: trimMode ? RED_CTA : GOLD, color: trimMode ? '#fff' : D, border: 'none',
                fontFamily: MONO, fontSize: 11, letterSpacing: '.1em',
                textTransform: 'uppercase', fontWeight: 700,
                cursor: annotationMode ? 'default' : 'pointer',
                opacity: annotationMode ? 0.4 : 1,
                transition: 'opacity .15s',
              }}
              onMouseEnter={e => { if (!annotationMode) e.currentTarget.style.opacity = '0.85' }}
              onMouseLeave={e => { if (!annotationMode) e.currentTarget.style.opacity = '1' }}
            >
              {trimMode ? <><X size={13} /> Annuler le découpage</> : <><Scissors size={13} /> Découper un clip</>}
              {!trimMode && Date.now() < CLIP_CTA_NEW_UNTIL && (
                <span style={{
                  position: 'absolute', top: -7, right: -7,
                  background: RED_CTA, color: '#fff',
                  fontFamily: MONO, fontSize: 7, fontWeight: 700, letterSpacing: '.1em',
                  padding: '2px 5px', borderRadius: 3, lineHeight: 1,
                }}>
                  NEW
                </span>
              )}
            </button>

            {/* Annoter la frame courante (toggle, comme Decouper) */}
            <button
              onClick={handleToggleAnnotation}
              aria-label={annotationMode ? 'Quitter l\'annotation' : 'Annoter cette frame'}
              aria-pressed={annotationMode}
              style={{
                position: 'relative',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                padding: '9px 16px',
                background: annotationMode ? RED_CTA : 'transparent',
                color: annotationMode ? '#fff' : CREAM,
                border: annotationMode ? 'none' : '1px solid rgba(245,242,235,0.3)',
                fontFamily: MONO, fontSize: 11, letterSpacing: '.1em',
                textTransform: 'uppercase', fontWeight: 700,
                cursor: 'pointer', transition: 'all .15s',
              }}
              onMouseEnter={e => { if (!annotationMode) e.currentTarget.style.borderColor = CREAM }}
              onMouseLeave={e => { if (!annotationMode) e.currentTarget.style.borderColor = 'rgba(245,242,235,0.3)' }}
            >
              {annotationMode ? <><X size={13} /> Quitter l'annotation</> : <><Pencil size={13} /> Annoter</>}
              {!annotationMode && Date.now() < ANNOTATE_NEW_UNTIL && (
                <span style={{
                  position: 'absolute', top: -7, right: -7,
                  background: RED_CTA, color: '#fff',
                  fontFamily: MONO, fontSize: 7, fontWeight: 700, letterSpacing: '.1em',
                  padding: '2px 5px', borderRadius: 3, lineHeight: 1,
                }}>
                  NEW
                </span>
              )}
            </button>

            {/* Secondaire : marquer un moment (bookmark simple) */}
            <button
              onClick={handleMarkClick}
              // Sans garde, empilait MomentMarker par-dessus l'overlay actif.
              disabled={annotationMode}
              aria-label="Marquer un moment"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                padding: '9px 16px',
                background: 'transparent', color: CREAM,
                border: '1px solid rgba(245,242,235,0.3)',
                fontFamily: MONO, fontSize: 11, letterSpacing: '.1em',
                textTransform: 'uppercase', fontWeight: 700,
                cursor: annotationMode ? 'default' : 'pointer',
                opacity: annotationMode ? 0.4 : 1,
                transition: 'all .15s',
              }}
              onMouseEnter={e => { if (!annotationMode) e.currentTarget.style.borderColor = CREAM }}
              onMouseLeave={e => { if (!annotationMode) e.currentTarget.style.borderColor = 'rgba(245,242,235,0.3)' }}
            >
              <Plus size={13} /> Marquer un moment
            </button>
          </div>
        </div>
      </div>

      {/* Player + panneau clips (flex row desktop, empile sur mobile) */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: 12, alignItems: 'stretch', animation: 'fadeUp .4s ease',
      }}>
        <div style={{ flex: 1, minWidth: 0, position: 'relative', overflow: 'hidden' }}>
          <VideoPlayer
            ref={playerRef}
            src={videoUrl}
            keyboardEnabled={!creatingMoment && !editingMoment && !trimMode && !annotationMode}
            hideControls={trimMode || annotationMode}
            onTimeUpdate={(t) => {
              setCurrentTime(prev => Math.abs(prev - t) >= 0.25 ? t : prev)
            }}
            onDurationChange={setDuration}
          >
            {/* Enfants du player, donc cales sur l'image reellement affichee et
                non sur la boite du player. Les deux couches partagent la meme
                reference : c'est la duplication de reference qui avait decale
                le mode lecture, la barre de controles s'ajoutant a la boite. */}

            {/* Mode lecture : la frame annotee, en lecture seule. La couche est
                en pointer-events: none (cf. AnnotationLayer), les controles du
                player restent donc pleinement utilisables pendant l'arret. */}
            {readingMode && activePoint && (
              <AnnotationLayer elements={activePoint.elements} />
            )}

            {/* Compte a rebours, en haut de l'image et centre — meme place que
                la toolbar du mode annotation, l'information de mode est au meme
                endroit dans les deux modes. En bas a gauche il recouvrait
                l'affichage du temps de la barre de controles. Cliquable :
                figer l'image annule le decompte, relancer enchaine. */}
            {readingMode && activePoint && (
              <button
                onClick={toggleHold}
                aria-label={countdown !== null ? 'Rester sur cette image' : 'Reprendre la lecture'}
                style={{
                  position: 'absolute', top: 12, left: '50%',
                  transform: 'translateX(-50%)', zIndex: 5,
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 12px', minHeight: 40, whiteSpace: 'nowrap',
                  background: 'rgba(0,0,0,0.78)', color: '#fff',
                  border: '1px solid rgba(255,255,255,0.18)', borderRadius: 8,
                  fontFamily: MONO, fontSize: 11, letterSpacing: '.08em',
                  cursor: 'pointer', backdropFilter: 'blur(8px)',
                }}
              >
                {countdown !== null ? <Pause size={13} /> : <Play size={13} />}
                {countdown !== null ? `Reprise dans ${countdown}s` : 'Reprendre'}
              </button>
            )}

            {/* Overlay d'annotation SVG sur la frame figee. */}
            {annotationMode && (
              <AnnotationOverlay
                // Remonte a chaque changement de moment cible : l'etat interne
                // (dessin, undo/redo) doit repartir de zero, pas etre recycle.
                // Durcissement : deux espaces de noms disjoints. L'ancienne
                // forme `id || frame-<t>` faisait retomber un moment sans id
                // dans l'espace des annotations libres, ou une collision
                // devenait possible.
                key={annotationMoment ? `m-${annotationMoment.id}` : `free-${annotationTimecode}`}
                playerRef={playerRef}
                videoDuration={duration}
                currentTime={annotationTimecode}
                matchId={matchId}
                moment={annotationMoment}
                onCancel={closeAnnotationMode}
                onSuccess={handleAnnotationSuccess}
                onDirtyChange={(d) => { annotationDirtyRef.current = d }}
                showToast={showToast}
              />
            )}
          </VideoPlayer>

          {/* Reste frere du player : la decoupe se pose volontairement sur le
              bas du player, barre de controles masquee, pas sur l'image. */}
          {trimMode && (
            <TrimOverlay
              playerRef={playerRef}
              videoUrl={videoUrl}
              videoDuration={duration}
              currentTime={clipTimecode}
              matchId={matchId}
              half={hasHalves ? activeHalf : undefined}
              onCancel={() => setTrimMode(false)}
              onSuccess={handleTrimSuccess}
              showToast={showToast}
            />
          )}

        </div>

        {/* Verrou par prop et non par NavLock : les deux racines de ClipsPanel
            sont des flex items avec flexShrink/alignSelf/width imposes,
            qu'un div intercalaire casserait. */}
        <ClipsPanel
          moments={moments}
          onSelectClip={handleSelectClip}
          onDownloadClip={handleDownloadClip}
          isOpen={panelOpen}
          onToggle={handleTogglePanel}
          isMobile={isMobile}
          locked={annotationMode}
          onShare={handleShareClips}
          sharing={sharing}
        />
      </div>

      {/* Timeline — marqueurs et piste de seek, verrouilles pendant l'annotation */}
      <NavLock locked={annotationMode}>
        <Timeline
          moments={timelineMoments}
          duration={duration}
          currentTime={currentTime}
          onMomentClick={(m) => {
            handleSeek(m.timecode_start)
          }}
          onSeek={handleSeek}
        />
      </NavLock>

      {/* Bandeau hint clavier — masque sur mobile (pas de clavier) */}
      {!isMobile && (
        <div style={{
          background: '#fff', border: `1px solid ${RULE}`, borderTop: 'none',
          padding: '10px 16px', display: 'flex', alignItems: 'center',
          gap: 14, flexWrap: 'wrap',
        }}>
          <span style={{
            fontFamily: MONO, fontSize: 9, letterSpacing: '.14em',
            textTransform: 'uppercase', color: MUTED,
          }}>
            Raccourcis
          </span>
          <Kbd>Espace</Kbd>
          <span style={{ fontFamily: MONO, fontSize: 10, color: MUTED }}>lecture / pause</span>
          <Kbd>←</Kbd> <Kbd>→</Kbd>
          <span style={{ fontFamily: MONO, fontSize: 10, color: MUTED }}>±10 secondes</span>
        </div>
      )}

      {/* Compteur global */}
      <div style={{
        background: '#fff', border: `1px solid ${RULE}`, borderTop: 'none',
        padding: '14px 16px',
      }}>
        <div style={{ fontFamily: DISP, fontSize: 20, color: INK, textTransform: 'uppercase', letterSpacing: '.01em' }}>
          {moments.length} moment{moments.length > 1 ? 's' : ''} marqué{moments.length > 1 ? 's' : ''}
        </div>
        <div style={{ fontFamily: MONO, fontSize: 10, color: MUTED, letterSpacing: '.06em', marginTop: 2 }}>
          dont {momentsForCard.length} pour les joueurs
        </div>
      </div>

      {/* Deux colonnes — clic de ligne (seek), crayon et fleche doree
          verrouilles pendant l'annotation. La liste reste lisible. */}
      <NavLock
        locked={annotationMode}
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: 12, marginTop: 12,
        }}
      >
        <MomentsList
          variant="private"
          title="Mes moments"
          moments={momentsPrivate}
          players={lineup}
          onSeek={handleSeek}
          onEdit={handleEditMoment}
          onMove={handleToggleForCard}
        />
        <MomentsList
          variant="for_card"
          title="Pour les joueurs"
          moments={momentsForCard}
          players={lineup}
          onSeek={handleSeek}
          onEdit={handleEditMoment}
          onMove={handleToggleForCard}
          onTogglePublic={handleTogglePublic}
        />
      </NavLock>

    </DashboardLayout>
  )
}

function Kbd({ children }) {
  return (
    <span style={{
      fontFamily: MONO, fontSize: 10, padding: '3px 8px',
      background: '#f5f2eb', border: `1px solid ${RULE}`,
      color: INK, letterSpacing: '.05em',
    }}>
      {children}
    </span>
  )
}
